import { useEffect, useState } from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CircularProgress from "@mui/material/CircularProgress";
import DocumentoConVisor, { MODAL_FONT } from "../../documentViewer/DocumentoConVisor";

/**
 * Sección "Área Documentos" del modal "Registro Candidato" que lee los
 * documentos desde el modelo NUEVO (job-application-service) identificando a la
 * persona por RUT, y los previsualiza/descarga con el MISMO mecanismo binario
 * que la sección de Postulaciones:
 *
 *   GET {APPLICANT}/application/person-documents?dni=<RUT>   -> lista
 *   GET {APPLICANT}/application/document-file/{id}?inline=true -> preview
 *   GET {APPLICANT}/application/document-file/{id}             -> descarga
 *
 * El backend hace stream del binario (nunca expone el SAS). No permite subir
 * documentos (la carga en este flujo está descontinuada), pero sí rotarlos y
 * guardar el giro (ver DocumentoConVisor).
 */

interface INewModelDocument {
  personDocumentId: string;
  documentType: string;
  originalFileName: string;
  mimeType: string;
  extension: string;
  size: number;
  blobUrl?: string;
  uploadedAt?: string;
}

interface IDocumentViewerNewModel {
  rut?: string;
  hiddenSection?: boolean;
}

export default (props: IDocumentViewerNewModel) => {
  const { rut, hiddenSection } = props;

  const appApiBase = process.env.REACT_APP_API_APPLICANT ?? "";

  const [hidden, setHidden] = useState<boolean>(hiddenSection ?? false);
  const [documents, setDocuments] = useState<INewModelDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Un solo visor abierto a la vez.
  const [viewingDocId, setViewingDocId] = useState<string | null>(null);

  // Carga de la lista de documentos por RUT
  useEffect(() => {
    if (!appApiBase || !rut) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    fetch(`${appApiBase}/application/person-documents?dni=${encodeURIComponent(rut)}`)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return (await res.json()) as { documents?: INewModelDocument[] };
      })
      .then((data) => {
        if (cancelled) return;
        setDocuments(Array.isArray(data?.documents) ? data.documents : []);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setDocuments([]);
        setError("No se pudieron cargar los documentos.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rut, appApiBase]);

  return (
    <>
      <section className={`seccion-title`} onClick={() => setHidden(!hidden)}>
        <span className="span-title">ÁREA DOCUMENTOS</span>
        <KeyboardArrowDownIcon className={hidden ? "disabled" : ""} />
      </section>
      {hidden && <span className="linea-separador-titulo"></span>}

      <section className={`project-table ${hidden ? "disabled" : ""}`}>
        {loading ? (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", padding: "26px 0" }}>
            <CircularProgress size={20} thickness={4} sx={{ color: "#0065DD" }} />
            <span style={{ fontFamily: MODAL_FONT, fontSize: "13px", color: "#64748b" }}>
              Cargando documentos…
            </span>
          </div>
        ) : error ? (
          <div style={{ fontFamily: MODAL_FONT, fontSize: "13px", color: "#b91c1c", textAlign: "center", padding: "18px 0" }}>
            {error}
          </div>
        ) : documents.length === 0 ? (
          <div style={{ fontFamily: MODAL_FONT, fontSize: "13px", color: "#94a3b8", textAlign: "center", padding: "18px 0" }}>
            Sin documentos adjuntos
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
            {documents.map((doc) => (
              <DocumentoConVisor
                key={doc.personDocumentId}
                doc={doc}
                open={viewingDocId === doc.personDocumentId}
                onToggle={() => setViewingDocId((id) => (id === doc.personDocumentId ? null : doc.personDocumentId))}
                onRotado={({ size }) => {
                  if (!size) return;
                  setDocuments((prev) => prev.map((d) => (d.personDocumentId === doc.personDocumentId ? { ...d, size } : d)));
                }}
              />
            ))}
          </div>
        )}
      </section>
    </>
  );
};
